/**
 * InvoiceFormHeader sub-components.
 *
 * Split out of InvoiceFormHeader.tsx to keep individual functions under
 * 50 lines and the file under 500.
 */

import type React from 'react'

import { countries } from '@elsapiens/ui'
import { clsx } from 'clsx'

import {
  Input,
  NumericInput,
  FormField,
  DatePicker,
  SearchableSelect,
  Select,
  CountrySelect,
  type SearchableSelectOption,
} from '../../ui'

import type { UseInvoiceFormReturn } from './useInvoiceForm'
import type { Account } from '../../../services/account-service'

// =============================================================================
// Refresh-rate spinner / icon
// =============================================================================

function SpinnerIcon(): JSX.Element {
  return (
    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        fill="currentColor"
      />
    </svg>
  )
}

function RefreshIcon(): JSX.Element {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
      />
    </svg>
  )
}

interface RefreshRateButtonProps {
  isLoading: boolean
  isFetchingRate: boolean
  onRefresh: () => void
}

export function RefreshRateButton({ isLoading, isFetchingRate, onRefresh }: RefreshRateButtonProps): JSX.Element {
  return (
    <button
      className="absolute right-[2px] top-[2px] bottom-[2px] w-8 font-medium text-primary hover:text-primary/90 hover:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed transition-colors flex items-center justify-center rounded-r"
      disabled={isLoading || isFetchingRate}
      title="Refresh exchange rate"
      type="button"
      onClick={onRefresh}
    >
      {isFetchingRate ? <SpinnerIcon /> : <RefreshIcon />}
    </button>
  )
}

// =============================================================================
// PO toggle button (used by PONumberField)
// =============================================================================

interface POToggleButtonProps {
  isLoading: boolean
  title: string
  onClick: () => void
  children: React.ReactNode
  style?: React.CSSProperties
}

export function POToggleButton({ isLoading, title, onClick, children, style }: POToggleButtonProps): JSX.Element {
  return (
    <button
      className="px-3 text-sm border border-border bg-surface hover:bg-muted flex items-center justify-center gap-1"
      disabled={isLoading}
      style={{
        whiteSpace: 'nowrap',
        minWidth: 'fit-content',
        borderTopRightRadius: '0.375rem',
        borderBottomRightRadius: '0.375rem',
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
        ...style,
      }}
      title={title}
      type="button"
      onClick={onClick}
    >
      {children}
    </button>
  )
}

export function EditIcon(): JSX.Element {
  return (
    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
      />
    </svg>
  )
}

export function ListIcon(): JSX.Element {
  return (
    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        d="M4 6h16M4 10h16M4 14h16M4 18h16"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
      />
    </svg>
  )
}

// =============================================================================
// PartySelectField helpers
// =============================================================================

interface QuickCreateFooterProps {
  partyLabel: string
  closeDropdown: () => void
  onOpenQuickCreate: () => void
}

function QuickCreateFooter({ partyLabel, closeDropdown, onOpenQuickCreate }: QuickCreateFooterProps): JSX.Element {
  return (
    <button
      className="w-full px-3 py-2 text-sm font-medium text-primary bg-muted hover:bg-accent rounded transition-colors flex items-center justify-center gap-2"
      type="button"
      onClick={() => { closeDropdown(); onOpenQuickCreate() }}
    >
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path d="M12 4v16m8-8H4" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
      </svg>
      Add New {partyLabel}
    </button>
  )
}

function renderPartyOption(
  option: SearchableSelectOption<string>,
  isSelected: boolean,
  isHighlighted: boolean,
): JSX.Element {
  return (
    <div
      className={clsx(
        'flex items-start gap-3 px-3 py-2.5 cursor-pointer transition-colors duration-150',
        isHighlighted && 'bg-accent',
        isSelected && 'bg-muted',
      )}
    >
      <div className="flex-shrink-0 text-2xl mt-0.5">
        {/* eslint-disable-next-line @typescript-eslint/no-base-to-string -- TODO(refactor): review and fix properly */}
        {option.data?.flag ? String(option.data.flag) : '🏳️'}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-foreground truncate">{option.label}</div>
        {option.description && (
          <div className="text-xs text-muted-foreground truncate mt-0.5">
            {option.description}
          </div>
        )}
      </div>
    </div>
  )
}

interface PartySearchableProps {
  form: UseInvoiceFormReturn
  isLoading: boolean
  isEditingPosted: boolean
  partyLabel: string
  onOpenQuickCreate: () => void
}

function PartySearchable({ form, isLoading, isEditingPosted, partyLabel, onOpenQuickCreate }: PartySearchableProps): JSX.Element {
  const partyOptions = (form as UseInvoiceFormReturn & { partyOptions?: SearchableSelectOption<string>[] }).partyOptions ?? []
  return (
    <div className="flex-1">
      <SearchableSelect
        hideMessage
        modal
        disabled={isLoading || isEditingPosted}
        footerAction={(closeDropdown) => (
          <QuickCreateFooter closeDropdown={closeDropdown} partyLabel={partyLabel} onOpenQuickCreate={onOpenQuickCreate} />
        )}
        options={partyOptions}
        placeholder={`Select ${partyLabel.toLowerCase()}`}
        renderOption={renderPartyOption}
        searchPlaceholder={`Search ${partyLabel.toLowerCase()}s...`}
        value={form.selectedPartyId}
        onChange={(value) => form.setSelectedPartyId(value ?? '')}
      />
    </div>
  )
}

interface WalkInToggleProps {
  isLoading: boolean
  isWalkIn: boolean
  form: UseInvoiceFormReturn
  setIsWalkIn: (v: boolean) => void
  setSelectedCashAccountId: (v: string) => void
}

function WalkInToggle({ isLoading, isWalkIn, form, setIsWalkIn, setSelectedCashAccountId }: WalkInToggleProps): JSX.Element {
  const handleClick = (): void => {
    setIsWalkIn(!isWalkIn)
    if (!isWalkIn) { form.setSelectedPartyId('') } else { setSelectedCashAccountId('') }
  }
  return (
    <button
      className={clsx(
        'flex-shrink-0 px-3 text-xs font-medium border rounded transition-colors whitespace-nowrap',
        isWalkIn
          ? 'bg-amber-100 border-amber-400 text-amber-800 hover:bg-amber-200 dark:bg-amber-900/30 dark:border-amber-600 dark:text-amber-300'
          : 'bg-surface border-border text-muted-foreground hover:bg-muted hover:text-foreground',
      )}
      disabled={isLoading}
      title={isWalkIn ? 'Switch to party invoice' : 'Switch to walk-in / cash'}
      type="button"
      onClick={handleClick}
    >
      {isWalkIn ? '💵 Cash' : 'Walk-In'}
    </button>
  )
}

interface PartySelectFieldProps {
  form: UseInvoiceFormReturn
  isLoading: boolean
  isEditingPosted: boolean
  isVendor: boolean
  partyLabel: string
  onOpenQuickCreate: () => void
}

function getPartyFieldLabel(isWalkIn: boolean, isVendor: boolean, partyLabel: string): string {
  if (!isWalkIn) { return partyLabel }
  return isVendor ? 'Cash/Bank Account (Payment)' : 'Cash/Bank Account (Receipt)'
}

export function PartySelectField({
  form, isLoading, isEditingPosted, isVendor, partyLabel, onOpenQuickCreate,
}: PartySelectFieldProps): JSX.Element {
  const { isWalkIn, setIsWalkIn, cashGLAccounts, selectedCashAccountId, setSelectedCashAccountId } = form
  const cashAccountOptions = cashGLAccounts.map((a: Account) => ({ value: a.id, label: `${a.code} – ${a.name}` }))
  const fieldLabel = getPartyFieldLabel(isWalkIn, isVendor, partyLabel)

  return (
    <FormField hideMessage required className="col-span-2" label={fieldLabel}>
      <div className="flex items-stretch gap-2">
        {isWalkIn ? (
          <div className="flex-1">
            <Select
              hideMessage
              disabled={isLoading}
              options={cashAccountOptions}
              placeholder="Select cash/bank account"
              value={selectedCashAccountId}
              onChange={setSelectedCashAccountId}
            />
          </div>
        ) : (
          <PartySearchable
            form={form}
            isEditingPosted={isEditingPosted}
            isLoading={isLoading}
            partyLabel={partyLabel}
            onOpenQuickCreate={onOpenQuickCreate}
          />
        )}
        {!isEditingPosted && (
          <WalkInToggle
            form={form}
            isLoading={isLoading}
            isWalkIn={isWalkIn}
            setIsWalkIn={setIsWalkIn}
            setSelectedCashAccountId={setSelectedCashAccountId}
          />
        )}
      </div>
    </FormField>
  )
}

// =============================================================================
// PONumberField (split into picker + plain mode)
// =============================================================================

interface PONumberFieldProps {
  form: UseInvoiceFormReturn
  isLoading: boolean
  partyLabel: string
  isVendor: boolean
}

function POSelectMode({ form, isLoading, isShowPOToggle }: { form: UseInvoiceFormReturn; isLoading: boolean; isShowPOToggle: boolean }): JSX.Element {
  const { customerPOOptions, selectedCustomerPOId, handleCustomerPOSelection, setIsCustomerPOSelectEnabled, setSelectedCustomerPOId, selectedParty } = form
  return (
    <>
      <div style={{ flex: 1, position: 'relative' }}>
        <style>{`
          .po-select-no-right-border button {
            border-top-right-radius: 0 !important;
            border-bottom-right-radius: 0 !important;
            border-right: 0 !important;
          }
        `}</style>
        <div className="po-select-no-right-border">
          <SearchableSelect
            hideMessage
            disabled={isLoading || !selectedParty}
            options={customerPOOptions}
            placeholder="Select PO..."
            searchPlaceholder="Search POs..."
            value={selectedCustomerPOId}
            onChange={(value) => handleCustomerPOSelection(value ?? '')}
          />
        </div>
      </div>
      {isShowPOToggle && (
        <POToggleButton
          isLoading={isLoading}
          title="Type manually"
          onClick={() => { setIsCustomerPOSelectEnabled(false); setSelectedCustomerPOId('') }}
        >
          <EditIcon />
          Type
        </POToggleButton>
      )}
    </>
  )
}

function POManualMode({ form, isLoading, isShowPOToggle }: { form: UseInvoiceFormReturn; isLoading: boolean; isShowPOToggle: boolean }): JSX.Element {
  const { poNumber, setPONumber, setIsCustomerPOSelectEnabled } = form
  return (
    <>
      <Input
        hideMessage
        className="truncate"
        disabled={isLoading}
        placeholder="Purchase Order Number"
        style={{
          flex: 1,
          ...(isShowPOToggle && {
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
          }),
        }}
        type="text"
        value={poNumber}
        onChange={(e) => setPONumber(e.target.value)}
      />
      {isShowPOToggle && (
        <POToggleButton
          isLoading={isLoading}
          style={{ borderLeft: 0 }}
          title="Select from POs"
          onClick={() => { setIsCustomerPOSelectEnabled(true); setPONumber('') }}
        >
          <ListIcon />
          Select
        </POToggleButton>
      )}
    </>
  )
}

export function PONumberField({ form, isLoading, isVendor }: PONumberFieldProps): JSX.Element {
  const { isCustomerPOSelectEnabled, customerPOs, selectedParty } = form
  const isShowPOToggle = Boolean(selectedParty && customerPOs.length > 0)
  const isInSelectMode = isCustomerPOSelectEnabled && customerPOs.length > 0
  return (
    <FormField hideMessage label={isVendor ? 'Our PO Number' : 'PO Number'}>
      <div style={{ display: 'flex', alignItems: 'stretch' }}>
        {isInSelectMode
          ? <POSelectMode form={form} isLoading={isLoading} isShowPOToggle={isShowPOToggle} />
          : <POManualMode form={form} isLoading={isLoading} isShowPOToggle={isShowPOToggle} />}
      </div>
    </FormField>
  )
}

// =============================================================================
// Reusable: export details card
// =============================================================================

interface ExportDetailsCardProps {
  form: UseInvoiceFormReturn
  isLoading: boolean
  onRefreshRate: () => void
}

function CurrencyAndRateFields({ form, isLoading, onRefreshRate }: ExportDetailsCardProps): JSX.Element {
  const { currency, setCurrency, exchangeRate, setExchangeRate, baseCurrency, currencyOptions,
    isFetchingRate, errors, currencyChangedByUser } = form
  return (
    <>
      <FormField hideMessage required label="Currency">
        <Select
          hideMessage
          disabled={isLoading}
          options={currencyOptions}
          value={currency}
          onChange={(value) => { currencyChangedByUser.current = true; setCurrency(value) }}
        />
      </FormField>
      {currency !== baseCurrency && (
        <FormField hideMessage required error={errors.exchangeRate} label={`Exchange Rate (1 unit = ${baseCurrency})`}>
          <div className="relative">
            <NumericInput
              hideMessage
              decimalPlaces={2}
              disabled={isLoading || isFetchingRate}
              placeholder={isFetchingRate ? 'Fetching rate...' : '0.00'}
              size="md"
              style={{ paddingRight: '2.5rem' }}
              value={exchangeRate}
              onChange={(e) => setExchangeRate(e.target.value)}
            />
            <RefreshRateButton isFetchingRate={isFetchingRate} isLoading={isLoading} onRefresh={onRefreshRate} />
          </div>
        </FormField>
      )}
    </>
  )
}

function LutAndCountryFields({ form, isLoading }: { form: UseInvoiceFormReturn; isLoading: boolean }): JSX.Element {
  const { dateFormat, countryOfDestinationCode, setCountryOfDestinationCode, setCountryOfDestination,
    lutNumber, setLutNumber, lutDate, setLutDate } = form
  return (
    <>
      <FormField hideMessage label="LUT Number">
        <Input
          hideMessage
          disabled={isLoading}
          placeholder="AD070123456789Z"
          type="text"
          value={lutNumber}
          onChange={(e) => setLutNumber(e.target.value)}
        />
      </FormField>
      <FormField hideMessage label="LUT Date">
        <DatePicker dateFormat={dateFormat} disabled={isLoading} value={lutDate} onChange={setLutDate} />
      </FormField>
      <FormField hideMessage required label="Country of Destination">
        <CountrySelect
          hideMessage
          disabled={isLoading}
          placeholder="Select destination country"
          value={countryOfDestinationCode}
          onChange={(countryCode) => {
            setCountryOfDestinationCode(countryCode ?? '')
            const name = countries.find((c) => c.code === countryCode)?.name ?? ''
            setCountryOfDestination(name)
          }}
        />
      </FormField>
    </>
  )
}

export function ExportDetailsCard(props: ExportDetailsCardProps): JSX.Element {
  return (
    <div className="card overflow-visible">
      <h3 className="text-xs font-bold tracking-wider uppercase el-px-md py-2 bg-primary-solid text-primary-foreground rounded-t">
        Export Details
      </h3>
      <div className="el-p-md">
        <div className="form-grid el-gap-field">
          <CurrencyAndRateFields {...props} />
          <LutAndCountryFields form={props.form} isLoading={props.isLoading} />
        </div>
      </div>
      <div className="el-px-md el-py-xs bg-amber-50 dark:bg-amber-900/20 border-t border-amber-400 dark:border-amber-600 text-amber-800 dark:text-amber-200 text-xs rounded-b">
        <strong>Note:</strong> Supply meant for Export under Letter of Undertaking without payment of IGST
      </div>
    </div>
  )
}
